<template>
  <div class="h-full">
    <!-- TraderSites Table -->
    <BaseTable
      v-if="traderSites"
      :headers="tableHeaders"
      :list="filteredTraderSites"
      table-id="trader-sites"
      clickable
      hide-grouping
      :total-records="traderSitesTotalCount"
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @delete-multiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="onRefreshData"
      @table-item-clicked="onEdit"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      is-saving-layout-hidden>
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="w-4 h-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5">
          <!-- Col 1 -->
          <div class="space-y-4 justify-self-start">
            <div>
              <BaseTriStateToggle
                :label="$t('representative.isMapped')"
                name="isInactiveFilter"
                v-model="filterIsMapped" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>

    <DCTraderSiteModalCreateEdit
      v-if="createEditModalActive && currentTraderSite"
      :trader-site="currentTraderSite"
      :mode="createEditModalMode"
      :view-only="createEditModalMode === 'view'"
      :representative-id="props.representativeId || ''"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import type { DCTraderSite, RepresentativeFiltersData } from '@/types/DataCollection';
import BaseTable from '../common/BaseTable.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import DCTraderSiteModalCreateEdit from './DCTraderSiteModalCreateEdit.vue';
import { deepCopy } from '@/utils/data';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { getDCTraderSitesWithConditions } from '@/services/api/dataCollection';
import BaseTriStateToggle from '../common/BaseTriStateToggle.vue';

const { t: $t } = useI18n();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  resetState: boolean;
  representativeId?: string;
}>();

const traderSites = defineModel<DCTraderSite[]>('traderSites', { default: [] });
const isTraderSiteModified = defineModel<boolean>('isTraderSiteModified', { default: false });

// Modal state
const createEditModalActive = ref(false);
const currentTraderSite = ref<DCTraderSite>();
const createEditModalMode = ref<'add' | 'edit' | 'view'>('add');

const originalTraderSites = ref<DCTraderSite[]>([]);

const traderSitesTotalCount = ref<number>(0);
const traderSitesItemsPerPage = ref(50);
const traderSitesPageNumber = ref(1);
const filterIsMapped = ref<boolean | undefined>(undefined);

const tableHeaders = [
  {
    value: 'traderSite.name',
    text: $t('customer.traderSiteName'),
    type: 'text',
    active: true,
  },
  {
    value: 'traderSite.code',
    text: $t('customer.traderSiteCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'traderSiteRemotedCode',
    text: $t('representative.remotedCode'),
    type: 'text',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const filteredTraderSites = computed<DCTraderSite[]>(() => {
  if (!traderSites.value) return [];
  return traderSites.value.filter((x) => x.recordMode !== RecordMode.Deleted);
});

watch(
  () => traderSites.value,
  (newData) => {
    isTraderSiteModified.value =
      JSON.stringify(newData) !== JSON.stringify(originalTraderSites.value);
  },
  { deep: true }
);

watch(
  () => props.resetState,
  async (newVal, oldVal) => {
    if (props.representativeId && (oldVal === undefined || (newVal && oldVal !== newVal))) {
      await loadTraderSites();
    }
  },
  { immediate: true }
);

async function loadTraderSites(
  params?: TableFilterData & {
    isMapped?: boolean;
  }
) {
  const queryParams = {
    page: params?.page || traderSitesPageNumber.value,
    itemsPerPage: params?.itemsPerPage || traderSitesItemsPerPage.value,
    query: params?.query || '',
    sortColumn: params?.column !== undefined ? tableHeaders[params.column]?.value : undefined,
    sortAscending: params?.order === 'asc' || params?.order === undefined,
    representativeId: props.representativeId,
    isMapped: params?.isMapped,
  };

  const res = await getDCTraderSitesWithConditions(queryParams);
  if (res) {
    originalTraderSites.value = res.data;
    traderSites.value = deepCopy<DCTraderSite[]>(originalTraderSites.value);
    traderSitesTotalCount.value = res.count;
  }
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DCTraderSite>();

function onDeleteRow(item: DCTraderSite) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (deleteRowItem.value && traderSites.value) {
    const index = traderSites.value.findIndex((x) => x.id === deleteRowItem.value!.id);
    if (index >= 0) {
      if (traderSites.value[index].recordMode === RecordMode.Added) {
        traderSites.value.splice(index, 1);
      } else {
        traderSites.value[index].recordMode = RecordMode.Deleted;
      }
    }
  }
  modalDeleteRow.value = false;
  deleteRowItem.value = undefined;
}

function onDeleteMultiselect(items: DCTraderSite[]) {
  if (!traderSites.value) return;

  const indicesToRemove: number[] = [];
  const indicesToMarkDeleted: number[] = [];

  items.forEach((item) => {
    const index = traderSites.value!.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      if (traderSites.value![index].recordMode === RecordMode.Added) {
        indicesToRemove.push(index);
      } else {
        indicesToMarkDeleted.push(index);
      }
    }
  });

  indicesToMarkDeleted.forEach((index) => {
    traderSites.value![index].recordMode = RecordMode.Deleted;
  });

  indicesToRemove
    .sort((a, b) => b - a)
    .forEach((index) => {
      traderSites.value!.splice(index, 1);
    });
}

async function onRefreshData(options: TableFilterData) {
  await loadTraderSites(options);
}

function initTraderSite(): DCTraderSite {
  return {
    id: uuidv4(),
    traderSiteId: '',
    representativeId: props.representativeId || '',
    traderSiteRemotedCode: '',
    traderSite: {},
    recordMode: RecordMode.Added,
  };
}

function onAddNew() {
  currentTraderSite.value = initTraderSite();
  createEditModalMode.value = 'add';
  createEditModalActive.value = true;
}

function onEdit(traderSite: DCTraderSite) {
  currentTraderSite.value = deepCopy(traderSite);
  createEditModalMode.value = props.mode === 'view' ? 'view' : 'edit';
  createEditModalActive.value = true;
}

function onSave(traderSite: DCTraderSite) {
  createEditModalActive.value = false;

  if (createEditModalMode.value === 'add') {
    traderSites.value.push(traderSite);
  } else {
    const index = traderSites.value.findIndex((x) => x.id === traderSite.id);
    if (index >= 0) {
      if (traderSites.value[index].recordMode !== RecordMode.Added) {
        traderSite.recordMode = RecordMode.Modified;
      }
      traderSites.value[index] = traderSite;
    }
  }
}

async function onApplyFilters(data: RepresentativeFiltersData) {
  data.isMapped = filterIsMapped.value;
  await onRefreshData(data);
}

async function onResetFilters() {
  filterIsMapped.value = undefined;
  const params = {
    itemsPerPage: 50,
    sortColumn: '',
    sortAscending: true,
    page: 1,
  };

  await onRefreshData(params);
}
</script>
